import { computed, readonly } from 'vue'

type ThemePreference = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'bittrip-theme'
const MEDIA_QUERY = '(prefers-color-scheme: dark)'

let isInitialized = false
let mediaQueryList: MediaQueryList | null = null
let mediaQueryListener: ((event: MediaQueryListEvent) => void) | null = null

const getSystemTheme = (): ResolvedTheme => {
  if (!import.meta.client) {
    return 'light'
  }
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light'
}

const updateThemeMetaColor = (theme: ResolvedTheme): void => {
  if (!import.meta.client) {
    return
  }
  const themeColor = document.querySelector('meta[name="theme-color"]')
  if (themeColor) {
    themeColor.setAttribute('content', theme === 'dark' ? '#0f172a' : '#1b252b')
  }
}

const applyResolvedTheme = (theme: ResolvedTheme): void => {
  const resolvedTheme = useState<ResolvedTheme>('theme.resolved', () => 'light')
  resolvedTheme.value = theme

  if (!import.meta.client) {
    return
  }

  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.setAttribute('data-theme', theme)
  document.documentElement.style.colorScheme = theme
  updateThemeMetaColor(theme)
}

const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference === 'system') {
    return getSystemTheme()
  }
  return preference
}

const setupSystemThemeListener = (): void => {
  if (!import.meta.client) {
    return
  }

  mediaQueryList = window.matchMedia(MEDIA_QUERY)
  if (mediaQueryListener) {
    return
  }

  mediaQueryListener = () => {
    const preference = useState<ThemePreference>('theme.preference', () => 'system')
    if (preference.value === 'system') {
      applyResolvedTheme(getSystemTheme())
    }
  }

  mediaQueryList.addEventListener('change', mediaQueryListener)
}

export const useThemeMode = () => {
  const preference = useState<ThemePreference>('theme.preference', () => 'system')
  const resolvedTheme = useState<ResolvedTheme>('theme.resolved', () => 'light')

  const initTheme = (): void => {
    if (!import.meta.client) {
      return
    }
    if (!isInitialized) {
      const storedPreference = localStorage.getItem(STORAGE_KEY)
      if (storedPreference === 'light' || storedPreference === 'dark' || storedPreference === 'system') {
        preference.value = storedPreference
      }
      setupSystemThemeListener()
      isInitialized = true
    }
    applyResolvedTheme(resolveTheme(preference.value))
  }

  const setThemePreference = (nextPreference: ThemePreference): void => {
    preference.value = nextPreference
    if (import.meta.client) {
      localStorage.setItem(STORAGE_KEY, nextPreference)
    }
    applyResolvedTheme(resolveTheme(nextPreference))
  }

  const toggleTheme = (): void => {
    setThemePreference(resolvedTheme.value === 'dark' ? 'light' : 'dark')
  }

  const isDark = computed(() => resolvedTheme.value === 'dark')

  return {
    preference: readonly(preference),
    resolvedTheme: readonly(resolvedTheme),
    isDark,
    initTheme,
    setThemePreference,
    toggleTheme
  }
}
